import { createSelector } from 'reselect'
import formatLesson from 'redux/selectors/helpers/lesson_formatter'
import { formatLibraryType } from 'helpers/libraryTypeFormatter'
import libraryTypes from 'helpers/enums/libraryTypes'
import featureFlagsSelectorCreator from 'redux/selectors/feature_flags/feature_flags_selector'
import parentsSelector from 'redux/selectors/parents/parents_selector'
import featureFlagsTypes from 'helpers/enums/featureFlagsTypes'
import shouldHideStoreSelector from 'redux/selectors/user/user_store_access_selector'
import tooltipTypes from 'helpers/enums/tooltipTypes'
import activityOnboardingSelector from 'redux/selectors/ui/activity_onboarding_selector'
import { getSearchRoute, addParamToUrl } from 'helpers/urlHandler'
import { canDragAndDrop, canShowSecondaryActions } from 'helpers/my_library/my_library'
import googleSlidesUnauthorizedUserSelector from 'redux/selectors/my_library/google_slides_unauthorized_user_selector'
import lessonSources from 'helpers/enums/lessonSources'
import lessonStatusTypes from 'helpers/enums/lessonStatusTypes'

const featureFlagsSelector = featureFlagsSelectorCreator([
	featureFlagsTypes.MICROSOFT_TEAMS_LIVE_LESSON,
	featureFlagsTypes.PAYWALLS_PROJECT,
	featureFlagsTypes.VIDEO_LESSON_STREAMLINE,
	featureFlagsTypes.TEACH_BUTTON_AND_MODAL,
	featureFlagsTypes.CREATE_MENU,
	featureFlagsTypes.EXPLORE_LIBRARY_THUMBNAIL,
	featureFlagsTypes.VIDEO_LESSON_TYPE,
	featureFlagsTypes.CREATE_LESSON_THUMBNAIL,
	featureFlagsTypes.ONBOARDING_GUIDE,
])

const lessonsSelector = state => state.lessons
const myLibrarySelector = state => state.myLibrary
const ltiSelector = state => state.lti
const userExtraSelector = state => state.user.extra
const userIdSelector = state => state.user.data.userId
const folderColors = state => state.folders.colors
const getUserFullDataLoaded = state => state.user.fullDataLoaded

const myLibraryViewSelector = createSelector(
	[	lessonsSelector,
		myLibrarySelector,
		ltiSelector,
		userExtraSelector,
		featureFlagsSelector,
		folderColors,
		userIdSelector,
		parentsSelector,
		shouldHideStoreSelector,
		activityOnboardingSelector,
		googleSlidesUnauthorizedUserSelector,
		getUserFullDataLoaded],
	(
		myLessons,
		myLibrary,
		lti,
		userExtra,
		featureFlags,
		colorsMenu,
		userId,
		parents,
		shouldHideStore,
		isOnboardingActivity,
		isGoogleSlidesBlocked,
		isUserFullDataLoaded
	) => {
		const defaultFolderColor = {id: '0', color: {r: '184', g: '192', b: '198'}}
		const folderColor =
			myLibrary.folderColor && colorsMenu.length > 0 ?
				colorsMenu.find(color => myLibrary.folderColor === color.id) :
				defaultFolderColor
		const folder = (myLibrary.folderUID && myLibrary.folderName) ?
			{
				id: myLibrary.folderUID,
				name: myLibrary.folderName,
				color: folderColor.color,
				canShare: myLibrary.canShareFolder,
			} : null
		const breadcrumb = [...parents]

		let enableOptions = {}
		let lessons = {}
		const libraryType = formatLibraryType(lti)
		const isNotInLti = libraryType === libraryTypes.REGULAR
		const isExtensionLibrary = libraryType === libraryTypes.EXTENSION

		if (folder) {
			const canShare = folder.canShare === '1'
			enableOptions = {
				add: { isGrayed: false},
				rename: { isGrayed: false},
				remove: { isGrayed: false},
				add_folder: { isGrayed: false},
				folder_color: { isGrayed: false},
			}
			breadcrumb.push(folder)
			if (isNotInLti) {
				enableOptions.share = {
					isGrayed: !canShare,
				}
			}
		}

		let tooltipType = tooltipTypes.FIRST_LESSON

		lessons = myLibrary.lessons.map(UID => {

			const lesson = myLessons[UID]
			const shouldUseTooltip = !lesson.data.isTutorial
			&& lesson.data.state === 1
			&& lesson.library_object_type === '1'
			&& libraryType === libraryTypes.REGULAR
			&& isOnboardingActivity

			let lessonTooltip
			if (shouldUseTooltip) {
				lessonTooltip = tooltipType
				tooltipType = tooltipTypes.NONE
			} else {
				lessonTooltip = tooltipTypes.NONE
			}

			const canLaunchSessionWithZoom = userExtra && userExtra.hasZoomEnabled
			const canLaunchSessionWithMSFTTeams = featureFlags[featureFlagsTypes.MICROSOFT_TEAMS_LIVE_LESSON]
			const canEditLesson = !isGoogleSlidesBlocked ||
				(lesson.data.state !== 0 && lesson.library_object_type === '1') ||
				lesson.data.sourceInfo.type !== lessonSources.GOOGLE_SLIDES

			return formatLesson(
				lesson,
				libraryType,
				true,
				lessonTooltip,
				canLaunchSessionWithZoom,
				canLaunchSessionWithMSFTTeams,
				false,
				canEditLesson,
			)
		})

		const shouldShowNoContentMessage =
			isExtensionLibrary && !lessons.length && !myLibrary.folders.some(folder => folder.presentationsCount)
		const storeSearchUrl = getSearchRoute(null, addParamToUrl('', 'q', myLibrary.input_value))
		const shouldShowNewCreateLessonThumbnail = featureFlags[featureFlagsTypes.VIDEO_LESSON_STREAMLINE]
		const shouldShowNewTeachButton = featureFlags[featureFlagsTypes.TEACH_BUTTON_AND_MODAL]
		const shouldShowCreate = isNotInLti && !featureFlags[featureFlagsTypes.CREATE_MENU]
		const shouldShowExploreLibraryThumbnail = isNotInLti && !shouldHideStore && myLibrary.folderUID === '0' &&
			featureFlags[featureFlagsTypes.EXPLORE_LIBRARY_THUMBNAIL]
		const shouldShowCreationMenu = featureFlags[featureFlagsTypes.CREATE_MENU] &&
			myLibrary.folderUID === '0' && isNotInLti
		const canCreateInteractiveVideo = featureFlags[featureFlagsTypes.VIDEO_LESSON_TYPE]
		const shouldShowCreateLessonThumbnail = isNotInLti &&
			myLibrary.folderUID === '0' &&
			featureFlags[featureFlagsTypes.CREATE_LESSON_THUMBNAIL] &&
			myLibrary.current.search.state === 'idle' &&
			isUserFullDataLoaded &&
			!lessons.find(
				lesson => lesson.data.published === lessonStatusTypes.PUBLISHED &&
				lesson.data.author.data.entityUID === userId
			)
		const shouldHideBreadcrumb = featureFlags[featureFlagsTypes.ONBOARDING_GUIDE] && myLibrary.folderUID === '0'

		return {
			lessons,
			query: myLibrary.input_value,
			order: myLibrary.order_field,
			folders: myLibrary.folders,
			currentFolderId: folder ? folder.id : 0,
			folder,
			breadcrumb,
			enableOptions,
			hasMore: myLibrary.current.search.more && !!lessons.length,
			searchState: myLibrary.current.search.state,
			showCreate: shouldShowCreate,
			canShowSecondaryActions: canShowSecondaryActions(libraryType),
			showExplore: !featureFlags[featureFlagsTypes.PAYWALLS_PROJECT] && userExtra &&
				userExtra.showExplore && isNotInLti && !shouldHideStore,
			showExploreFree: !featureFlags[featureFlagsTypes.PAYWALLS_PROJECT] && userExtra && userExtra.showExploreFree
				&& isNotInLti && !shouldHideStore && !isOnboardingActivity,
			showLongNoResultsMessage: isNotInLti && !shouldHideStore,
			canDragAndDrop: canDragAndDrop(libraryType),
			userId,
			colorsMenu,
			folderColor: folderColor.id,
			shouldShowNoContentMessage,
			storeSearchUrl,
			shouldShowDropdown: !isExtensionLibrary,
			shouldShowNewCreateLessonThumbnail,
			shouldShowNewTeachButton,
			shouldShowCreationMenu,
			shouldShowExploreLibraryThumbnail,
			canCreateInteractiveVideo,
			shouldShowCreateLessonThumbnail,
			shouldHideBreadcrumb,
		}
	}
)

export default myLibraryViewSelector
